import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Map markdown elements to design-system token classes (no raw colors).
// Spacing is tuned for a comfortable reading rhythm inside a chat bubble.
const components: Components = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target="_blank" rel="noreferrer" className="break-words text-accent underline" />
  ),
  p: ({ node: _node, ...props }) => (
    <p {...props} className="my-2 leading-relaxed first:mt-0 last:mb-0" />
  ),
  strong: ({ node: _node, ...props }) => <strong {...props} className="font-semibold" />,
  em: ({ node: _node, ...props }) => <em {...props} className="italic" />,

  h1: ({ node: _node, ...props }) => (
    <h1 {...props} className="mb-2 mt-4 text-lg font-bold first:mt-0" />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 {...props} className="mb-2 mt-4 text-md font-bold first:mt-0" />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 {...props} className="mb-1.5 mt-3 text-base font-semibold first:mt-0" />
  ),
  h4: ({ node: _node, ...props }) => (
    <h4 {...props} className="mb-1.5 mt-3 text-base font-semibold first:mt-0" />
  ),

  ul: ({ node: _node, ...props }) => (
    <ul {...props} className="my-2 flex list-disc flex-col gap-1 pl-5 first:mt-0 last:mb-0" />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol {...props} className="my-2 flex list-decimal flex-col gap-1 pl-5 first:mt-0 last:mb-0" />
  ),
  li: ({ node: _node, ...props }) => <li {...props} className="leading-relaxed [&>ul]:my-1 [&>ol]:my-1" />,

  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      {...props}
      className="my-2 border-l-2 border-border-strong pl-3 text-fg-secondary first:mt-0 last:mb-0"
    />
  ),
  hr: ({ node: _node, ...props }) => <hr {...props} className="my-3 border-border" />,

  code: ({ node: _node, ...props }) => (
    <code {...props} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]" />
  ),
  pre: ({ node: _node, ...props }) => (
    <pre
      {...props}
      className="my-2 overflow-x-auto rounded-md bg-muted p-3 text-[0.85em] leading-normal first:mt-0 last:mb-0"
    />
  ),

  // GFM tables — the wrapper scrolls horizontally so a wide table keeps its
  // natural column widths (slides left/right) instead of squishing cell text
  // in a narrow bubble/panel. `min-w-0` lets the scroll container bound itself.
  table: ({ node: _node, ...props }) => (
    <div className="my-2 max-w-full min-w-0 overflow-x-auto first:mt-0 last:mb-0">
      <table {...props} className="w-max min-w-full border-collapse text-[0.95em]" />
    </div>
  ),
  thead: ({ node: _node, ...props }) => <thead {...props} />,
  th: ({ node: _node, ...props }) => (
    <th
      {...props}
      className="whitespace-nowrap border border-border bg-muted px-2.5 py-1.5 text-left align-top font-semibold"
    />
  ),
  td: ({ node: _node, ...props }) => (
    <td {...props} className="whitespace-nowrap border border-border px-2.5 py-1.5 align-top" />
  ),
};

/** Renders assistant message text as GitHub-flavored markdown, token-styled. */
export function MarkdownMessage({ text }: { text: string }) {
  return (
    <div className="min-w-0 text-base leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
